import * as React from "react";
import { connect } from "react-redux";
import { RouteComponentProps } from "react-router";
import { Action, Dispatch } from "redux";
import { createClientIpoCampaignActionCreator } from "../action/client_ipo_campaign";
import ClientIpoCampaignComponent from "../component/client/campaign/client_ipo_campaign";
import {
  IClientIpoCampaignDispatcher,
  createClientIpoCampaignDispatcher,
} from "../dispatcher/client_ipo_campaign";
import { GetCustomersByIpoCampaignPageRequest } from "../request/customer_request";
import { AppState } from "../store/app_state";
import { ClientIpoCampaignState } from "../store/client_ipo_campaign_store";

interface IProps extends RouteComponentProps<{}> {
  state: ClientIpoCampaignState;
  dispatcher: IClientIpoCampaignDispatcher;
}

interface IState {
  isInit: boolean;
}

// 開発・STG環境には本番で固定表示するエキスパートが存在しないため、
// 本番と同じ枚数になるようダミーユーザーを並べて固定表示する。
// 本番のみ指定されたエキスパートを表示する。
const DEV_DUMMY_CUSTOMER_ID = "I6xc7Rg717Q8AxpSwmDbIpuynyr2";

const fixedCustomerIDs = (forProd: string[]): string[] => {
  if (process.env.REACT_APP_LIBERA_ENV === "prod") {
    return forProd;
  }
  return forProd.map(() => DEV_DUMMY_CUSTOMER_ID);
};

export class ClientIpoCampaignContainer extends React.Component<
  IProps,
  IState
> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      isInit: true,
    };
  }

  public componentDidMount() {
    const req: GetCustomersByIpoCampaignPageRequest =
      new GetCustomersByIpoCampaignPageRequest();

    // ① IPOに向けた組織づくり・管理体制の整備
    // IPO準備体制の構築
    req.ipoPrepCustomerIDs = fixedCustomerIDs([
      "GcO7SViscQZYXhIflewMvaCckgi1", // 茂木久裕
      "d5q5JmW25cM5hwC8HiOzkgV5ilU2", // 太田英昭
      "5GJ73gCnj7hrNFOMLc3JTuA9qkW2", // 岡田雅史
      "D5kZLnuagCS4zU68Ns27fRfKhc93", // 小田晃司
    ]);

    // 内部統制、ガバナンス体制
    req.internalControlCustomerIDs = fixedCustomerIDs([
      "7avU15VQ95PaEGD6XCkWx0PESfu1", // 平川秀年
      "u1aAXujLFVahPPbAYPrtnsepGB03", // 長嶋邦英
      "xJqV4SPkOtOKHWs8nWKFz1QV2qk1", // 平岡晃
      "vyeKFlUpoDPx6GumXPUINZ0pt9G3", // 立花隼
    ]);

    // 監査
    req.auditCustomerIDs = fixedCustomerIDs([
      "OFVXhGH2ovdeRntOy83LdS9ZH6c2", // 川村英樹
      "a28mqyZuNJRglymoji621rKrF0j2", // 江原準一
    ]);

    // 証券会社、監査法人の対応
    req.securitiesAuditCustomerIDs = fixedCustomerIDs([
      "HYHNkSskIDdG5rgZxLACzj4GlY23", // 浅利圭佑
      "rlJ0qeVwtMaOelMGejsA5DJg8bo1", // 笹野誠
    ]);

    // ② IPO準備における資金調達・資本政策
    // 資金調達
    req.fundraisingCustomerIDs = fixedCustomerIDs([
      "cpYNlpNWUeYBz5mHbi2Tr9S8zqt1", // 林直介
      "Gs4OWjtpeqekO5pCPbkiHqUeYUE3", // 鹿島幸裕
      "nlXx5eXz1GUr93cpMQh6iTd25dX2", // 中辻仁
      "vBFzmXcCaKfpiSFSrc5we8QgACm1", // 土井裕介
    ]);

    // ③ 株価を上げるためのIR戦略
    // IR戦略
    req.irStrategyCustomerIDs = fixedCustomerIDs([
      "0cCJWfmsi1dHZ3KruEaqYIj5bni1", // 小林道法
      "VX11WofLE6NUGvpzTqGezoA9xMV2", // 山本高太郎
      "WYVfSFajyIO1jzCdOcfxjnErfHs1", // 渡邊将志
      "PBUP0wFR7mgdanC5GZ1PY54kwi22", // 及川慶
    ]);

    // 投資家対応
    req.investorRelationsCustomerIDs = fixedCustomerIDs([
      "Ru8JIbug3SaSpeB4GIFm5qlsCMo2", // 久野聡太
      "w97QgLAtAYggoi258m2PPuA5sJ72", // 三嶋義明
      "mtfOpk6t3bUpJEVWOiwboyTnImg1", // 伊藤修次郎
      "7tqSUiaylDSLl6CeiZqcJX2J96d2", // 菊川淳
    ]);

    // 説明資料作成
    req.materialsCustomerIDs = fixedCustomerIDs([
      "QtdaPzeoATdGyGVx7ORtob5OLAJ3", // 真鍋順子
      "PV1ipwX1VBg34L63KEY7MyJsSZC2", // 栗原智晴
    ]);

    // グローバル対応
    req.globalCustomerIDs = fixedCustomerIDs([
      "YPqQJlIct8UaeNn3wrYCPdd4TYu2", // 金誠智
      "0XYFghZ5VdhcphsEzxwNW8ukmb73", // 福澤秀一
    ]);

    // ④ IPO・IR領域に強いCxO・経営企画人材
    // CFO、経営企画など
    req.cfoCustomerIDs = fixedCustomerIDs([
      "RaFnpA6Vtdase8eACQwvSu1PvTw1", // 北川裕憲
      "V2H1wHNNFNeJr2pGbMLxNnLhxgy2", // 東貴志
      "7avU15VQ95PaEGD6XCkWx0PESfu1", // 平川秀年
      "srJHMouKVrfMq9SnXqcbACIcSzH3", // 須田仁之
    ]);

    this.props.dispatcher.getCustomers(req);
  }

  public componentDidUpdate(prevProps: IProps, prevState: IState) {
    // init処理の終了
    const isInitFinished: boolean =
      this.state.isInit &&
      !this.props.state.isLoading &&
      prevProps.state.isLoading;
    if (isInitFinished) {
      return this.setState({ isInit: false });
    }
  }

  public render(): JSX.Element {
    const { state } = this.props;
    const {
      ipoPrepCustomers,
      internalControlCustomers,
      auditCustomers,
      securitiesAuditCustomers,
      fundraisingCustomers,
      irStrategyCustomers,
      investorRelationsCustomers,
      materialsCustomers,
      globalCustomers,
      cfoCustomers,
      jobCareersWithCustomerID,
      clientBookmarks,
      isLoading,
    } = state;

    return (
      <ClientIpoCampaignComponent
        isLoading={isLoading}
        ipoPrepCustomers={ipoPrepCustomers}
        internalControlCustomers={internalControlCustomers}
        auditCustomers={auditCustomers}
        securitiesAuditCustomers={securitiesAuditCustomers}
        fundraisingCustomers={fundraisingCustomers}
        irStrategyCustomers={irStrategyCustomers}
        investorRelationsCustomers={investorRelationsCustomers}
        materialsCustomers={materialsCustomers}
        globalCustomers={globalCustomers}
        cfoCustomers={cfoCustomers}
        clientBookmarks={clientBookmarks}
        jobCareersWithCustomerID={jobCareersWithCustomerID}
      />
    );
  }
}

const mapStateToProps = (state: AppState) => {
  return {
    state: state.clientIpoCampaignReducer,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) => {
  return {
    dispatcher: createClientIpoCampaignDispatcher(
      dispatch,
      createClientIpoCampaignActionCreator()
    ),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ClientIpoCampaignContainer);



// WEBPACK FOOTER //
// ./src/presentation/container/client_ipo_campaign_container.tsx